import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { CheckCircle2, ArrowRight } from 'lucide-react';

const SuccessPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const ctx = gsap.context(() => {
            gsap.from('.success-element', {
                y: 30,
                opacity: 0,
                duration: 0.8,
                stagger: 0.1,
                ease: 'power3.out'
            });

            // Pulse animation for the checkmark
            gsap.to('.success-check', {
                scale: 1.1,
                opacity: 0.8,
                duration: 2,
                repeat: -1,
                yoyo: true,
                ease: "power1.inOut"
            });
        });

        // Load HubSpot Embed script
        const script = document.createElement('script');
        script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
        script.type = 'text/javascript';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            ctx.revert();
            if (document.body.contains(script)) {
                // In a true SPA, you might consider removing the script
                // but for HubSpot embed, leaving it ensures it doesn't break if navigated back.
            }
        };
    }, []);

    return (
        <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] flex flex-col items-center justify-center relative overflow-hidden selection:bg-[#7B61FF] selection:text-[#F0EFF4] pt-20 pb-32">
            {/* Background elements */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#10B981] opacity-[0.03] blur-[150px] rounded-full mix-blend-screen pointer-events-none"></div>

            <div className="max-w-3xl px-6 w-full text-center relative z-10">

                <div className="success-element flex justify-center mb-8">
                    <div className="relative">
                        <div className="absolute inset-0 bg-[#10B981] blur-xl opacity-20 rounded-full success-check"></div>
                        <CheckCircle2 className="w-24 h-24 text-[#10B981] relative z-10" />
                    </div>
                </div>

                <div className="success-element inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 text-xs font-semibold tracking-wider text-[#10B981] mb-6">
                    TRANSACTION VERIFIED
                </div>

                <h1 className="success-element text-4xl md:text-6xl font-bold tracking-tight mb-6">
                    Build Slot <span className="font-drama italic text-[#10B981] font-normal tracking-normal">Secured.</span>
                </h1>

                <p className="success-element text-lg text-[#F0EFF4]/70 mb-12 max-w-xl mx-auto leading-relaxed">
                    Your payment has been successfully processed. The RoofClaw engineering team has been notified and hardware acquisition protocols have begun.
                </p>

                <div className="success-element bg-[#18181B] border border-[#10B981]/20 rounded-[2rem] p-8 text-left mb-12 shadow-[0_0_30px_rgba(16,185,129,0.05)]">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-[#10B981]/10 flex items-center justify-center text-[#10B981] font-data text-xs shadow-[0_0_10px_rgba(16,185,129,0.2)]">01</span>
                        Immediate Next Steps
                    </h3>
                    <p className="text-[#F0EFF4]/70 mb-6 text-sm">
                        Please select an available time on the calendar below for your mandatory 1-Hour Onboarding Consult. Within the next 24 hours, you will also receive an encrypted email detailing:
                    </p>
                    <ul className="space-y-3 font-data text-xs text-[#F0EFF4]/60">
                        <li className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></div>
                            Hardware shipping timeline & tracking limits
                        </li>
                        <li className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></div>
                            Prerequisite API checklist for the Node Handshake
                        </li>
                    </ul>
                </div>

                {/* Calendar Embed */}
                <div className="success-element w-full bg-[#18181B] border border-[#10B981]/20 rounded-[2rem] overflow-hidden p-6 mb-12 shadow-[0_0_40px_rgba(16,185,129,0.05)]">
                    <h3 className="text-xl font-bold mb-6 text-left">Schedule Your 1-Hour Onboarding Consult</h3>
                    <div className="bg-white rounded-xl overflow-hidden min-h-[700px]">
                        <div className="meetings-iframe-container" data-src="https://www.roofingbusinesspartner.com/meetings/adamsandlinks/roofclaw-consultation?embed=true"></div>
                    </div>
                </div>

                <div className="success-element">
                    <Link to="/process" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-transparent border border-[#F0EFF4]/20 text-[#F0EFF4] font-bold tracking-wide hover:border-[#10B981] hover:text-[#10B981] transition-all duration-300 group">
                        Review Deployment Protocol
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SuccessPage;
